// People with access (M5 plan D10): invite an accountant or bookkeeper by email, see who has
// access, remove access. Owners and admins only.
import { type FormEvent, useState } from 'react';
import { useOrgData } from './accountant/useOrgData.js';
import type { Api } from './api.js';

const ROLE_NAMES: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin (everything, including people)',
  bookkeeper: 'Bookkeeper (sorts transactions and receipts)',
  accountant_edit: 'Accountant (can make changes and lodge)',
  accountant_read: 'Accountant (read only)',
};

interface People {
  members: { user_id: string; email: string; role: string; me: boolean }[];
  invitations: { id: string; email: string; role: string }[];
}

export function PeoplePanel({ api, orgId, role }: { api: Api; orgId: string; role: string }) {
  const people = useOrgData<People>(api, orgId, '/people');
  const [email, setEmail] = useState('');
  const [newRole, setNewRole] = useState('accountant_edit');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const canManage = role === 'owner' || role === 'admin';

  async function act(path: string, body?: unknown) {
    setError('');
    setNote('');
    try {
      await api.send(orgId, 'POST', path, body ?? {});
      await people.reload();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      return false;
    }
  }
  async function invite(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    if (await act('/people', { email, role: newRole })) {
      setNote(
        `Invitation sent. ${email} gets an email with a temporary password, sets up two-step sign-in, and then sees this business.`,
      );
      setEmail('');
    }
    setBusy(false);
  }

  if (!people.data) return <p className="muted">Loading</p>;
  return (
    <section className="stack bank-card">
      <h2>People with access</h2>
      <table className="table">
        <tbody>
          {people.data.members.map((m) => (
            <tr key={m.user_id}>
              <td>
                {m.email}
                {m.me ? ' (you)' : ''}
              </td>
              <td>{ROLE_NAMES[m.role] ?? m.role}</td>
              <td>
                {canManage && !m.me && m.role !== 'owner' && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => act(`/people/${m.user_id}/remove`)}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
          {people.data.invitations.map((i) => (
            <tr key={i.id} className="muted">
              <td>{i.email} (invited)</td>
              <td>{ROLE_NAMES[i.role] ?? i.role}</td>
              <td>
                {canManage && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => act(`/invitations/${i.id}/revoke`)}
                  >
                    Cancel
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {canManage && (
        <form className="stack" onSubmit={invite}>
          <div className="grid-2">
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <label>
              What they can do
              <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                {[
                  'accountant_edit',
                  'accountant_read',
                  'bookkeeper',
                  ...(role === 'owner' ? ['admin'] : []),
                ].map((r) => (
                  <option key={r} value={r}>
                    {ROLE_NAMES[r]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy}>
            Invite
          </button>
        </form>
      )}
      {note && <p>{note}</p>}
      {error && <p className="error">{error}</p>}
      <SupportAccess api={api} orgId={orgId} role={role} />
    </section>
  );
}

/** Foderis support access (M7 plan D7): the owner grants it for a day or a week and sees every request. */
export function SupportAccess({ api, orgId, role }: { api: Api; orgId: string; role: string }) {
  const support = useOrgData<{ until: string | null; actions: { at: string; request: string }[] }>(
    api,
    orgId,
    '/support',
  );
  const [error, setError] = useState('');
  if (!support.data) return null;
  const { until, actions } = support.data;
  async function act(path: string, body?: unknown) {
    setError('');
    try {
      await api.send(orgId, 'POST', path, body ?? {});
      await support.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    }
  }
  return (
    <section className="stack">
      <h2>Foderis support</h2>
      <p className="muted">
        Foderis cannot see your books unless you allow it. Access is read-only, ends by itself, and
        every page support opens is listed here.
      </p>
      {until ? (
        <p>
          Support can see your books until {new Date(until).toLocaleString('en-AU')}.{' '}
          {role === 'owner' && (
            <button type="button" className="link" onClick={() => act('/support/end')}>
              End now
            </button>
          )}
        </p>
      ) : (
        role === 'owner' && (
          <div className="title-row wrap">
            <button type="button" className="link" onClick={() => act('/support', { days: 1 })}>
              Allow for 24 hours
            </button>
            <button type="button" className="link" onClick={() => act('/support', { days: 7 })}>
              Allow for 7 days
            </button>
          </div>
        )
      )}
      {actions.length > 0 && (
        <table className="table">
          <caption>What support looked at</caption>
          <tbody>
            {actions.slice(0, 20).map((a) => (
              <tr key={`${a.at}-${a.request}`}>
                <td className="muted">{new Date(a.at).toLocaleString('en-AU')}</td>
                <td>{a.request}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
